/**
 * Advanced disclosure. Screen spec 4.8.
 *
 * Simple mode is the design. Advanced is a layer on it. This component is the
 * layer: it never removes, never reorders, never replaces, and it never
 * renders above the Simple content, because the whole promise is that the
 * simple thing is the real thing and the details are additional.
 *
 * It remembers nothing. Not in state, not in storage, not across a route
 * change. Advanced mode is the persistence mechanism, and a disclosure that
 * quietly remembered would compete with it and make the account level switch
 * feel broken.
 *
 * When Advanced is on globally the trigger is not rendered at all. The content
 * is simply present, expanded, with a flat chip marking it as the extra layer.
 * A control that expands something already expanded is furniture.
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/disclosure.css';

export interface AdvancedDisclosureProps {
  /** The specific variant, when there is one: Show the build log. */
  label?: string;
  children: ReactNode;
  /**
   * The first expand offer from spec 4.8. Shown once per account by the caller
   * that owns that fact. This component does not track it, by design.
   */
  offerAdvanced?: boolean;
  /** Forces the open state. The kit gallery uses it to show both stills. */
  defaultOpen?: boolean;
}

export function AdvancedDisclosure({
  label,
  children,
  offerAdvanced = false,
  defaultOpen = false,
}: AdvancedDisclosureProps) {
  const [advanced, setAdvanced] = useAdvanced();
  const [open, setOpen] = useState(defaultOpen);

  const offer =
    offerAdvanced && !advanced ? (
      <p className="disc__offer">
        {t('Want these details everywhere?')}{' '}
        <button
          type="button"
          className="disc__offer-action"
          onClick={() => {
            captureEvent('advanced_mode_enabled_from_disclosure', { label });
            setAdvanced(true);
          }}
        >
          {t('Turn on Advanced')}
        </button>
      </p>
    ) : null;

  if (advanced) {
    return (
      <div className="disc" data-mode="advanced">
        <span className="disc__chip">{t('ADVANCED')}</span>
        <div className="disc__content">{children}</div>
      </div>
    );
  }

  return (
    <div className="disc" data-mode="simple">
      <button
        type="button"
        className="disc__trigger"
        aria-expanded={open}
        onClick={() => {
          const next = !open;
          captureEvent('advanced_disclosure_toggled', { label: label ?? 'Show technical details', open: next });
          setOpen(next);
        }}
      >
        <span className="disc__chevron" data-open={open ? 'yes' : 'no'} aria-hidden="true">
          <svg viewBox="0 0 16 16" width="10" height="10" focusable="false">
            <path
              d="M6 3.5 10.5 8 6 12.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {label ?? t('Show technical details')}
      </button>

      {/* The grid rows trick: 0fr to 1fr animates a height the browser
          computed, so the content is never measured in JavaScript and never
          jumps at the end of the transition. */}
      <div className="disc__wrap" data-open={open ? 'yes' : 'no'}>
        <div className="disc__clip">
          <div className="disc__content">
            {children}
            {offer}
          </div>
        </div>
      </div>
    </div>
  );
}
